/* =========================================================
   KAPONO — Storefront behavior
   ========================================================= */
(function () {
  'use strict';

  document.documentElement.classList.remove('no-js');

  var body = document.body;
  var reducedMotion = window.matchMedia && window.matchMedia('(prefers-reduced-motion: reduce)').matches;
  var focusableSelector = [
    'a[href]',
    'button:not([disabled])',
    'input:not([disabled])',
    'select:not([disabled])',
    'textarea:not([disabled])',
    '[tabindex]:not([tabindex="-1"])'
  ].join(',');

  function getFocusable(container) {
    if (!container) return [];
    return Array.prototype.slice.call(container.querySelectorAll(focusableSelector))
      .filter(function (element) {
        return !element.hasAttribute('hidden') && element.getAttribute('aria-hidden') !== 'true';
      });
  }

  function trapFocus(container, event) {
    if (event.key !== 'Tab') return;
    var focusable = getFocusable(container);
    if (!focusable.length) {
      event.preventDefault();
      container.focus();
      return;
    }

    var first = focusable[0];
    var last = focusable[focusable.length - 1];
    if (event.shiftKey && document.activeElement === first) {
      event.preventDefault();
      last.focus();
    } else if (!event.shiftKey && document.activeElement === last) {
      event.preventDefault();
      first.focus();
    }
  }

  function setPageLocked(locked) {
    body.classList.toggle('is-locked', locked);
  }

  function parseJsonResponse(response) {
    return response.json().then(function (data) {
      if (!response.ok) {
        throw new Error(data.description || data.message || 'Something went wrong. Please try again.');
      }
      return data;
    });
  }

  function formatMoney(cents) {
    var currency = body.dataset.currency || 'USD';
    var locale = body.dataset.locale || document.documentElement.lang || 'en';
    try {
      return new Intl.NumberFormat(locale, {
        style: 'currency',
        currency: currency
      }).format(cents / 100);
    } catch (error) {
      return (cents / 100).toFixed(2) + ' ' + currency;
    }
  }

  /* ---------- Header scroll state ---------- */
  var header = document.querySelector('[data-kap-header]');
  var headerTicking = false;
  function updateHeaderState() {
    headerTicking = false;
    if (!header) return;
    if (window.scrollY > 40) {
      header.classList.add('is-solid');
    } else if (header.dataset.transparent === 'true') {
      header.classList.remove('is-solid');
    }
  }
  function requestHeaderUpdate() {
    if (!headerTicking) {
      window.requestAnimationFrame(updateHeaderState);
      headerTicking = true;
    }
  }
  window.addEventListener('scroll', requestHeaderUpdate, { passive: true });
  updateHeaderState();

  /* ---------- Mobile menu ---------- */
  var hamburger = document.querySelector('[data-kap-hamburger]');
  var mobileMenu = document.querySelector('[data-kap-mobile-menu]');
  var mobileClose = document.querySelector('[data-kap-mobile-close]');
  var mobileMenuReturnFocus = null;

  function openMobileMenu() {
    if (!mobileMenu) return;
    if (window.KapCart) window.KapCart.close(false);
    mobileMenuReturnFocus = document.activeElement;
    mobileMenu.classList.add('is-open');
    mobileMenu.setAttribute('aria-hidden', 'false');
    if (hamburger) hamburger.setAttribute('aria-expanded', 'true');
    setPageLocked(true);
    window.requestAnimationFrame(function () {
      (mobileClose || mobileMenu).focus();
    });
  }

  function closeMobileMenu(restoreFocus) {
    if (!mobileMenu) return;
    mobileMenu.classList.remove('is-open');
    mobileMenu.setAttribute('aria-hidden', 'true');
    if (hamburger) hamburger.setAttribute('aria-expanded', 'false');
    setPageLocked(false);
    if (restoreFocus !== false && mobileMenuReturnFocus && document.contains(mobileMenuReturnFocus)) {
      mobileMenuReturnFocus.focus();
    }
  }

  if (hamburger) hamburger.addEventListener('click', openMobileMenu);
  if (mobileClose) mobileClose.addEventListener('click', function () { closeMobileMenu(true); });
  if (mobileMenu) {
    mobileMenu.querySelectorAll('a').forEach(function (link) {
      link.addEventListener('click', function () { closeMobileMenu(false); });
    });
    mobileMenu.addEventListener('keydown', function (event) {
      if (event.key === 'Escape') {
        event.preventDefault();
        closeMobileMenu(true);
      } else {
        trapFocus(mobileMenu, event);
      }
    });
  }
  window.addEventListener('resize', function () {
    if (window.innerWidth > 900 && mobileMenu && mobileMenu.classList.contains('is-open')) {
      closeMobileMenu(false);
    }
  });

  /* ---------- Fade-in on scroll ---------- */
  var fadeEls = document.querySelectorAll('.kap-fade');
  if (body.classList.contains('animations-enabled') && !reducedMotion && 'IntersectionObserver' in window && fadeEls.length) {
    var fadeObserver = new IntersectionObserver(
      function (entries) {
        entries.forEach(function (entry) {
          if (entry.isIntersecting) {
            entry.target.classList.add('is-visible');
            fadeObserver.unobserve(entry.target);
          }
        });
      },
      { threshold: 0.12 }
    );
    fadeEls.forEach(function (element) { fadeObserver.observe(element); });
  } else {
    fadeEls.forEach(function (element) { element.classList.add('is-visible'); });
  }

  /* ---------- Back to top ---------- */
  var backToTop = document.querySelector('[data-kap-back-to-top]');
  if (backToTop) {
    var backToTopTicking = false;
    function updateBackToTop() {
      backToTopTicking = false;
      var visible = window.scrollY > 600;
      backToTop.classList.toggle('is-visible', visible);
      backToTop.setAttribute('aria-hidden', visible ? 'false' : 'true');
      backToTop.tabIndex = visible ? 0 : -1;
    }
    window.addEventListener('scroll', function () {
      if (!backToTopTicking) {
        window.requestAnimationFrame(updateBackToTop);
        backToTopTicking = true;
      }
    }, { passive: true });
    backToTop.addEventListener('click', function () {
      window.scrollTo({ top: 0, behavior: reducedMotion ? 'auto' : 'smooth' });
    });
    updateBackToTop();
  }

  /* ---------- Category filter (optional, current page only) ---------- */
  var filterBar = document.querySelector('[data-kap-filter-bar]');
  if (filterBar) {
    var filterButtons = filterBar.querySelectorAll('[data-filter]');
    var productCards = document.querySelectorAll('[data-kap-product-card]');
    filterButtons.forEach(function (button) {
      button.addEventListener('click', function () {
        var filter = button.getAttribute('data-filter');
        filterButtons.forEach(function (candidate) {
          var active = candidate === button;
          candidate.classList.toggle('is-active', active);
          candidate.setAttribute('aria-pressed', active ? 'true' : 'false');
        });
        productCards.forEach(function (card) {
          var tags = (card.getAttribute('data-tags') || '')
            .split(',')
            .map(function (tag) { return tag.trim().toLowerCase(); });
          card.hidden = filter !== 'all' && tags.indexOf(filter) === -1;
        });
      });
    });
  }

  /* ---------- Product media ---------- */
  document.querySelectorAll('[data-kap-product-media]').forEach(function (wrapper) {
    var thumbnails = wrapper.querySelectorAll('[data-kap-thumb]');
    var mediaPanels = wrapper.querySelectorAll('[data-kap-media-panel]');

    function activateMedia(mediaId) {
      mediaPanels.forEach(function (panel) {
        var active = panel.getAttribute('data-media-id') === String(mediaId);
        panel.hidden = !active;
        panel.classList.toggle('is-active', active);
        if (!active) {
          panel.querySelectorAll('video').forEach(function (video) { video.pause(); });
        }
      });
      thumbnails.forEach(function (candidate) {
        var active = candidate.getAttribute('data-media-id') === String(mediaId);
        candidate.classList.toggle('is-active', active);
        candidate.setAttribute('aria-pressed', active ? 'true' : 'false');
      });
    }

    wrapper.kapActivateMedia = activateMedia;
    thumbnails.forEach(function (thumbnail) {
      thumbnail.addEventListener('click', function () {
        activateMedia(thumbnail.getAttribute('data-media-id'));
      });
    });
  });

  /* ---------- Quantity steppers ---------- */
  document.querySelectorAll('[data-kap-quantity]').forEach(function (wrapper) {
    var input = wrapper.querySelector('input');
    var minus = wrapper.querySelector('[data-qty-minus]');
    var plus = wrapper.querySelector('[data-qty-plus]');
    if (!input) return;

    function normalizeQuantity() {
      var minimum = parseInt(input.min || '1', 10);
      var value = parseInt(input.value || String(minimum), 10);
      input.value = Math.max(minimum, isNaN(value) ? minimum : value);
      if (minus) minus.disabled = parseInt(input.value, 10) <= minimum;
    }

    if (minus) {
      minus.addEventListener('click', function () {
        input.value = parseInt(input.value || '1', 10) - 1;
        normalizeQuantity();
        input.dispatchEvent(new Event('change', { bubbles: true }));
      });
    }
    if (plus) {
      plus.addEventListener('click', function () {
        input.value = parseInt(input.value || '1', 10) + 1;
        normalizeQuantity();
        input.dispatchEvent(new Event('change', { bubbles: true }));
      });
    }
    input.addEventListener('change', normalizeQuantity);
    normalizeQuantity();
  });

  /* ---------- Variant picker ---------- */
  var productSection = document.querySelector('[data-kap-product-section]');
  if (productSection) {
    var variantScript = document.querySelector('[data-kap-product-json]');
    var variants = [];
    try {
      variants = variantScript ? JSON.parse(variantScript.textContent) : [];
    } catch (error) {
      variants = [];
    }

    var optionInputs = productSection.querySelectorAll('[data-kap-option-input]');
    var variantIdInput = productSection.querySelector('[data-kap-variant-id]');
    var priceElement = productSection.querySelector('[data-kap-price]');
    var addToBagButton = productSection.querySelector('[data-kap-main-atc]');
    var stickyButton = document.querySelector('[data-kap-sticky-button]');
    var stickyPriceElement = document.querySelector('[data-kap-sticky-atc] .kap-product-price');
    var productMedia = productSection.querySelector('[data-kap-product-media]');

    function selectedOptions() {
      var selected = [];
      optionInputs.forEach(function (input) {
        if (input.checked) {
          selected[parseInt(input.getAttribute('data-option-position'), 10) - 1] = input.value;
        }
      });
      return selected;
    }

    function matchingVariant() {
      var selected = selectedOptions();
      return variants.find(function (variant) {
        var options = [variant.option1, variant.option2, variant.option3];
        return selected.every(function (value, index) {
          return value === undefined || value === options[index];
        });
      });
    }

    function renderPrice(container, variant) {
      if (!container) return;
      while (container.firstChild) container.removeChild(container.firstChild);
      if (variant.compare_at_price && variant.compare_at_price > variant.price) {
        var regularPrice = document.createElement('s');
        regularPrice.textContent = formatMoney(variant.compare_at_price);
        container.appendChild(regularPrice);
        container.appendChild(document.createTextNode(formatMoney(variant.price)));
      } else {
        container.textContent = formatMoney(variant.price);
      }
    }

    function updateForVariant(variant) {
      if (!variant) {
        [addToBagButton, stickyButton].forEach(function (button) {
          if (!button) return;
          button.disabled = true;
          button.textContent = 'Unavailable';
        });
        return;
      }

      if (variantIdInput) variantIdInput.value = variant.id;
      renderPrice(priceElement, variant);
      if (stickyPriceElement) stickyPriceElement.textContent = formatMoney(variant.price);

      [addToBagButton, stickyButton].forEach(function (button) {
        if (!button) return;
        button.disabled = !variant.available;
        button.textContent = variant.available ? 'Add to Bag' : 'Sold Out';
      });

      if (productMedia && productMedia.kapActivateMedia && variant.featured_media) {
        productMedia.kapActivateMedia(variant.featured_media.id);
      }

      if (window.history && window.history.replaceState) {
        var url = new URL(window.location.href);
        url.searchParams.set('variant', variant.id);
        window.history.replaceState({}, '', url.toString());
      }
    }

    optionInputs.forEach(function (input) {
      input.addEventListener('change', function () {
        var group = input.closest('.kap-swatches');
        if (group) {
          group.querySelectorAll('.kap-swatch').forEach(function (swatch) {
            swatch.classList.remove('is-selected');
          });
        }
        var label = productSection.querySelector('label[for="' + input.id + '"]');
        if (label) label.classList.add('is-selected');
        updateForVariant(matchingVariant());
      });
    });
  }

  /* ---------- Sticky mobile add to cart ---------- */
  var mainAddToBag = document.querySelector('[data-kap-main-atc]');
  var stickyAddToBag = document.querySelector('[data-kap-sticky-atc]');
  var stickyAddToBagButton = document.querySelector('[data-kap-sticky-button]');
  if (stickyAddToBagButton && mainAddToBag) {
    stickyAddToBagButton.addEventListener('click', function () { mainAddToBag.click(); });
  }
  if (mainAddToBag && stickyAddToBag && 'IntersectionObserver' in window) {
    var addToBagObserver = new IntersectionObserver(function (entries) {
      entries.forEach(function (entry) {
        if (entry.isIntersecting) {
          stickyAddToBag.classList.remove('is-visible');
        } else if (entry.boundingClientRect.top < 0) {
          stickyAddToBag.classList.add('is-visible');
        }
      });
    }, { threshold: 0 });
    addToBagObserver.observe(mainAddToBag);
  }

  /* ---------- Account recovery form ---------- */
  var recoveryToggle = document.querySelector('[data-kap-recover-toggle]');
  var recoveryForm = document.querySelector('[data-kap-recover-form]');
  var loginForm = document.querySelector('[data-kap-login-form]');
  if (recoveryToggle && recoveryForm && loginForm) {
    function showRecoveryForm() {
      loginForm.hidden = true;
      recoveryForm.classList.add('is-visible');
      var recoveryEmail = recoveryForm.querySelector('input[type="email"]');
      if (recoveryEmail) recoveryEmail.focus();
    }
    recoveryToggle.addEventListener('click', function (event) {
      event.preventDefault();
      window.history.replaceState({}, '', '#recover');
      showRecoveryForm();
    });
    if (window.location.hash === '#recover') showRecoveryForm();
  }

  document.querySelectorAll('select[data-default]').forEach(function (select) {
    if (select.dataset.default) select.value = select.dataset.default;
  });

  document.querySelectorAll('[data-confirm-message]').forEach(function (form) {
    form.addEventListener('submit', function (event) {
      if (!window.confirm(form.getAttribute('data-confirm-message'))) {
        event.preventDefault();
      }
    });
  });

  /* =========================================================
     Cart drawer
     ========================================================= */
  var KapCart = {
    drawer: document.querySelector('[data-kap-cart-drawer]'),
    overlay: document.querySelector('[data-kap-cart-overlay]'),
    cartBody: document.querySelector('[data-kap-cart-body]'),
    footer: document.querySelector('[data-kap-cart-footer]'),
    subtotal: document.querySelector('[data-kap-cart-subtotal]'),
    status: document.querySelector('[data-kap-cart-status]'),
    countElements: document.querySelectorAll('[data-kap-cart-count]'),
    returnFocus: null,

    init: function () {
      var self = this;

      document.querySelectorAll('[data-kap-cart-open]').forEach(function (button) {
        button.addEventListener('click', function (event) {
          event.preventDefault();
          self.open(button);
        });
      });
      document.querySelectorAll('[data-kap-cart-close]').forEach(function (button) {
        button.addEventListener('click', function () { self.close(true); });
      });
      if (this.overlay) {
        this.overlay.addEventListener('click', function () { self.close(true); });
      }

      if (!this.drawer) return;

      document.addEventListener('submit', function (event) {
        var form = event.target;
        if (form.matches && form.matches('[data-kap-product-form]')) {
          event.preventDefault();
          self.addItem(form);
        }
      });

      if (this.cartBody) {
        this.cartBody.addEventListener('click', function (event) {
          var removeButton = event.target.closest('[data-kap-remove-line]');
          var minusButton = event.target.closest('[data-kap-line-minus]');
          var plusButton = event.target.closest('[data-kap-line-plus]');
          if (removeButton) {
            self.updateLine(removeButton.getAttribute('data-key'), 0);
          } else if (minusButton) {
            self.updateLine(
              minusButton.getAttribute('data-key'),
              Math.max(0, parseInt(minusButton.getAttribute('data-qty'), 10) - 1)
            );
          } else if (plusButton) {
            self.updateLine(
              plusButton.getAttribute('data-key'),
              parseInt(plusButton.getAttribute('data-qty'), 10) + 1
            );
          }
        });
      }

      this.drawer.addEventListener('keydown', function (event) {
        if (event.key === 'Escape') {
          event.preventDefault();
          self.close(true);
        } else {
          trapFocus(self.drawer, event);
        }
      });
    },

    announce: function (message, isError) {
      if (this.status) {
        this.status.textContent = '';
        this.status.classList.toggle('is-error', Boolean(isError));
        window.setTimeout(function () {
          if (KapCart.status) KapCart.status.textContent = message;
        }, 20);
      }
    },

    setBusy: function (busy) {
      if (!this.drawer) return;
      if (busy) {
        this.drawer.setAttribute('aria-busy', 'true');
      } else {
        this.drawer.removeAttribute('aria-busy');
      }
    },

    open: function (trigger) {
      if (!this.drawer) {
        window.location.href = body.dataset.cartUrl ? body.dataset.cartUrl.replace(/\.js$/, '') : '/cart';
        return;
      }
      if (mobileMenu && mobileMenu.classList.contains('is-open')) closeMobileMenu(false);
      this.returnFocus = trigger || document.activeElement;
      this.drawer.classList.add('is-open');
      this.drawer.setAttribute('aria-hidden', 'false');
      if (this.overlay) {
        this.overlay.classList.add('is-open');
        this.overlay.setAttribute('aria-hidden', 'false');
      }
      document.querySelectorAll('[data-kap-cart-open]').forEach(function (button) {
        button.setAttribute('aria-expanded', 'true');
      });
      setPageLocked(true);
      var closeButton = this.drawer.querySelector('[data-kap-cart-close]');
      window.requestAnimationFrame(function () {
        (closeButton || KapCart.drawer).focus();
      });
    },

    close: function (restoreFocus) {
      if (!this.drawer) return;
      this.drawer.classList.remove('is-open');
      this.drawer.setAttribute('aria-hidden', 'true');
      if (this.overlay) {
        this.overlay.classList.remove('is-open');
        this.overlay.setAttribute('aria-hidden', 'true');
      }
      document.querySelectorAll('[data-kap-cart-open]').forEach(function (button) {
        button.setAttribute('aria-expanded', 'false');
      });
      setPageLocked(false);
      if (restoreFocus !== false && this.returnFocus && document.contains(this.returnFocus)) {
        this.returnFocus.focus();
      }
    },

    addItem: function (form) {
      var formData = new FormData(form);
      var submitButton = form.querySelector('[type="submit"]');
      var productStatus = form.querySelector('[data-kap-product-status]');
      var previousLabel = submitButton ? submitButton.textContent : '';

      if (productStatus) productStatus.textContent = '';
      if (submitButton) {
        submitButton.disabled = true;
        submitButton.setAttribute('aria-busy', 'true');
        submitButton.textContent = 'Adding…';
      }

      function restoreButton() {
        if (!submitButton) return;
        submitButton.disabled = false;
        submitButton.removeAttribute('aria-busy');
        submitButton.textContent = previousLabel;
      }

      fetch(body.dataset.cartAddUrl || '/cart/add.js', {
        method: 'POST',
        headers: { Accept: 'application/json' },
        body: formData
      })
        .then(parseJsonResponse)
        .then(function () { return KapCart.refresh(); })
        .then(function () {
          restoreButton();
          KapCart.announce('Added to your bag.', false);
          KapCart.open(submitButton);
        })
        .catch(function (error) {
          restoreButton();
          var message = error.message || 'Unable to add this item. Please try again.';
          if (productStatus) productStatus.textContent = message;
          KapCart.announce(message, true);
        });
    },

    updateLine: function (key, quantity) {
      this.setBusy(true);
      fetch(body.dataset.cartChangeUrl || '/cart/change.js', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
        body: JSON.stringify({ id: key, quantity: quantity })
      })
        .then(parseJsonResponse)
        .then(function (cart) {
          KapCart.render(cart);
          KapCart.announce('Bag updated.', false);
        })
        .catch(function (error) {
          KapCart.announce(error.message || 'Unable to update your bag.', true);
        })
        .then(function () { KapCart.setBusy(false); });
    },

    refresh: function () {
      return fetch(body.dataset.cartUrl || '/cart.js', {
        headers: { Accept: 'application/json' }
      })
        .then(parseJsonResponse)
        .then(function (cart) {
          KapCart.render(cart);
          return cart;
        });
    },

    createLine: function (item) {
      var line = document.createElement('div');
      line.className = 'kap-cart-line';

      var media = document.createElement('div');
      media.className = 'kap-cart-line-media';
      if (item.image) {
        var image = document.createElement('img');
        image.src = item.image;
        image.alt = item.title || item.product_title || '';
        image.loading = 'lazy';
        image.width = 160;
        image.height = 200;
        media.appendChild(image);
      }
      line.appendChild(media);

      var details = document.createElement('div');
      var title = document.createElement('a');
      title.className = 'kap-cart-line-title';
      title.href = item.url || '#';
      title.textContent = item.product_title || item.title || '';
      details.appendChild(title);

      if (item.variant_title && item.variant_title !== 'Default Title') {
        var variant = document.createElement('div');
        variant.className = 'kap-cart-line-variant';
        variant.textContent = item.variant_title;
        details.appendChild(variant);
      }

      var price = document.createElement('div');
      price.className = 'kap-cart-line-price';
      price.textContent = formatMoney(item.final_price);
      details.appendChild(price);

      var quantity = document.createElement('div');
      quantity.className = 'kap-cart-line-qty';
      var minus = document.createElement('button');
      minus.type = 'button';
      minus.textContent = '−';
      minus.dataset.kapLineMinus = '';
      minus.dataset.key = item.key;
      minus.dataset.qty = item.quantity;
      minus.setAttribute('aria-label', 'Decrease quantity of ' + (item.product_title || item.title || 'item'));
      quantity.appendChild(minus);

      var quantityValue = document.createElement('span');
      quantityValue.textContent = item.quantity;
      quantityValue.setAttribute('aria-label', 'Quantity ' + item.quantity);
      quantity.appendChild(quantityValue);

      var plus = document.createElement('button');
      plus.type = 'button';
      plus.textContent = '+';
      plus.dataset.kapLinePlus = '';
      plus.dataset.key = item.key;
      plus.dataset.qty = item.quantity;
      plus.setAttribute('aria-label', 'Increase quantity of ' + (item.product_title || item.title || 'item'));
      quantity.appendChild(plus);
      details.appendChild(quantity);
      line.appendChild(details);

      var remove = document.createElement('button');
      remove.type = 'button';
      remove.className = 'kap-cart-line-remove';
      remove.dataset.kapRemoveLine = '';
      remove.dataset.key = item.key;
      remove.textContent = 'Remove';
      remove.setAttribute('aria-label', 'Remove ' + (item.product_title || item.title || 'item') + ' from bag');
      line.appendChild(remove);

      return line;
    },

    render: function (cart) {
      this.countElements.forEach(function (element) {
        element.textContent = cart.item_count;
      });
      if (!this.cartBody) return;

      while (this.cartBody.firstChild) this.cartBody.removeChild(this.cartBody.firstChild);

      if (!cart.items.length) {
        var empty = document.createElement('p');
        empty.className = 'kap-cart-drawer-empty';
        empty.textContent = 'Your bag is empty.';
        this.cartBody.appendChild(empty);
        if (this.footer) this.footer.hidden = true;
        return;
      }

      if (this.footer) this.footer.hidden = false;
      cart.items.forEach(function (item) {
        KapCart.cartBody.appendChild(KapCart.createLine(item));
      });
      if (this.subtotal) this.subtotal.textContent = formatMoney(cart.total_price);
    }
  };

  KapCart.init();
  window.KapCart = KapCart;
})();
